/**
 * What to offer where HeapsterLive checkout starts: the Pricing page's
 * HeapsterLive card and the Dashboard's subscription card (#2218).
 *
 * This is guidance, not the guard. `POST /v1/checkout/session` refuses a
 * second subscription with a 409 whatever a client shows. The point here is
 * that Subscribe is offered only when the state is known and says "not
 * subscribed".
 */

import type { CheckoutConflictReason } from '../../lib/checkoutConflict';
import type { UserSubscription } from './types';

/** HeapsterLive's entitlement. */
export const HEAPSTERLIVE_ENTITLEMENT_ID = 'hlive:access';

/** Shown when checkout could not be started and the failure has no message. */
export const CHECKOUT_START_FAILED_MESSAGE =
  'Failed to start checkout process. Please try again.';

export type HeapsterLiveCheckoutState =
  /** Nobody is signed in. Subscribe leads to sign-in. */
  | 'signed-out'
  /** The state is not known yet. Subscribe stays disabled. */
  | 'loading'
  /** The state could not be read. Subscribe stays disabled. */
  | 'error'
  /** A Stripe-billed subscription. Offer the billing portal. */
  | 'subscribed'
  /** HeapsterLive access that Stripe does not bill (Supapass, a manual grant). */
  | 'access-without-subscription'
  /** Known, and not subscribed. Offer Subscribe. */
  | 'not-subscribed';

/**
 * What a checkout attempt came to, and whose attempt it was. A page can stay
 * mounted while the signed-in account changes (a sign-in in another tab
 * reaches this one), so the outcome is kept with the uid it was raised for.
 */
export interface CheckoutOutcome {
  uid: string;
  /** The backend refused: a HeapsterLive subscription already exists. */
  conflict?: CheckoutConflictReason;
  /** Any other failure, as the line to show. */
  error?: string;
}

/**
 * The outcome to show the signed-in user: theirs, or none. This is also what
 * drops an answer that arrives after the account changed, since it is stored
 * under the uid that asked.
 */
export function checkoutOutcomeFor(
  outcome: CheckoutOutcome | null,
  uid: string | undefined
): CheckoutOutcome | null {
  return outcome && outcome.uid === uid ? outcome : null;
}

/** The parts of `useAuth` and `useSubscription` the state is read from. */
export interface HeapsterLiveCheckoutInput {
  signedIn: boolean;
  isLoading: boolean;
  error: Error | null;
  subscription: UserSubscription | null;
}

export function heapsterLiveCheckoutState({
  signedIn,
  isLoading,
  error,
  subscription,
}: HeapsterLiveCheckoutInput): HeapsterLiveCheckoutState {
  if (!signedIn) {
    return 'signed-out';
  }
  if (isLoading) {
    return 'loading';
  }
  if (error) {
    return 'error';
  }
  // Nothing listed and no entitlements. (A listed subscription whose
  // entitlements came back empty is not this: `useSubscription` reports that
  // as `error`.)
  if (!subscription) {
    return 'not-subscribed';
  }

  // Entitlements that were never loaded say nothing either way.
  // `useSubscription` always loads them; this is for any other caller, since
  // the type leaves them optional.
  if (!subscription.entitlements) {
    return 'error';
  }

  const entitlements = Object.values(subscription.entitlements);

  // The rule the Dashboard's subscription card already used: any active
  // entitlement that an active Stripe record backs.
  if (entitlements.some(e => e.isActive && e.hasStripeSubscription)) {
    return 'subscribed';
  }

  if (subscription.entitlements[HEAPSTERLIVE_ENTITLEMENT_ID]?.isActive) {
    return 'access-without-subscription';
  }

  return 'not-subscribed';
}
